import { Event } from "../lib/types";
import { parseISO, isSameDay, isAfter, isBefore, isWithinInterval, startOfDay, endOfDay, differenceInDays, addDays } from 'date-fns';
import { format } from 'date-fns';

// Processed event type with position information
export interface ProcessedEvent {
  event: Event;
  startDayIndex: number;
  endDayIndex: number;
  verticalPosition: number;
}

/**
 * Process multi-day events and assign vertical positions to avoid overlapping
 * with cascading layout for overlapping events
 */
export function processMultiDayEvents(events: Event[], days: Date[]): ProcessedEvent[] {
  if (!events.length || !days.length) return [];

  // Sort multi-day events: first by start date, then by duration (longer events first)
  const multiDayEvents = [...events].sort((a, b) => {
    const startA = parseISO(a.dateStart);
    const startB = parseISO(b.dateStart);
    const endA = parseISO(a.dateEnd);
    const endB = parseISO(b.dateEnd);

    // First sort by start date
    if (startA.getTime() !== startB.getTime()) {
      return startA.getTime() - startB.getTime();
    }

    // If start dates are equal, sort by duration (longer first)
    const durationA = endA.getTime() - startA.getTime();
    const durationB = endB.getTime() - startB.getTime();
    return durationB - durationA;
  });

  // Get the first and last visible day in our calendar
  const calendarStartDate = startOfDay(days[0]);
  const calendarEndDate = endOfDay(days[days.length - 1]);

  // Process each multi-day event to find its position in the calendar
  const processedEvents: ProcessedEvent[] = [];

  // Track occupied slots per day
  const occupiedSlots: number[][] = Array(days.length)
    .fill(null)
    .map(() => []);

  // Process each multi-day event
  multiDayEvents.forEach(event => {
    const eventStart = startOfDay(parseISO(event.dateStart));
    const eventEnd = endOfDay(parseISO(event.dateEnd));

    // Skip events completely outside our visible range
    if (eventEnd < calendarStartDate || eventStart > calendarEndDate) {
      return;
    }

    // Calculate visible start day in our calendar
    let visibleStartDay = eventStart;
    if (eventStart < calendarStartDate) {
      visibleStartDay = calendarStartDate;
    }

    // Calculate visible end day in our calendar
    let visibleEndDay = eventEnd;
    if (eventEnd > calendarEndDate) {
      visibleEndDay = calendarEndDate;
    }

    // Find the exact day indices in our calendar array
    let startDayIndex = -1;
    let endDayIndex = -1;

    for (let i = 0; i < days.length; i++) {
      const currentDay = startOfDay(days[i]);

      // Find first visible day
      if (isSameDay(currentDay, visibleStartDay)) {
        startDayIndex = i;
      }

      // Find last visible day
      if (isSameDay(currentDay, visibleEndDay)) {
        endDayIndex = i;
      }

      // If we've found both, we can stop
      if (startDayIndex !== -1 && endDayIndex !== -1) {
        break;
      }
    }

    // Skip if event doesn't fit in visible calendar
    if (startDayIndex === -1 || endDayIndex === -1) {
      return;
    }

    // Find the first available vertical position for this event
    let verticalPosition = 0;
    let positionFound = false;

    // Find the first vertical position where this event fits
    while (!positionFound) {
      positionFound = true;

      // Check each day this event spans
      for (let dayIndex = startDayIndex; dayIndex <= endDayIndex; dayIndex++) {
        // If this vertical position is already taken on this day
        if (occupiedSlots[dayIndex].includes(verticalPosition)) {
          positionFound = false;
          verticalPosition++;
          break;
        }
      }
    }

    // Mark all days as occupied for this vertical position
    for (let dayIndex = startDayIndex; dayIndex <= endDayIndex; dayIndex++) {
      occupiedSlots[dayIndex].push(verticalPosition);
    }

    // Add the processed event
    processedEvents.push({
      event,
      startDayIndex,
      endDayIndex,
      verticalPosition
    });
  });

  return processedEvents;
}

/**
 * Create a map to track which days have events (either single or multi-day)
 */
export function createDaysWithEventsMap(
  processedMultiDayEvents: ProcessedEvent[],
  days: Date[]
): Map<string, boolean> {
  const daysWithEvents = new Map<string, boolean>();

  // Mark days with multi-day events
  processedMultiDayEvents.forEach(({ startDayIndex, endDayIndex }) => {
    for (let i = startDayIndex; i <= endDayIndex; i++) {
      if (i >= 0 && i < days.length) {
        const dayKey = format(days[i], 'yyyy-MM-dd');
        daysWithEvents.set(dayKey, true);
      }
    }
  });

  return daysWithEvents;
}

/**
 * Calculate maximum vertical position used across all days
 */
export function getMaxVerticalPosition(processedEvents: ProcessedEvent[]): number {
  if (processedEvents.length === 0) return -1;
  return Math.max(...processedEvents.map(event => event.verticalPosition));
}
